import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';
import type { AppState } from '~/reducers';
import logger from '~/utils/logger/logger';
import { selectMapObject } from '~/ducks/area-details/reducer';
import { NativeCommandEnum, WebCommandEnum } from '../models/commands';
import { dispatchNativeCommand, setActiveRoutePath, setActiveRouteSearch } from '../store/reducer';
import {
  decodeAndParseBase64Utf8String,
  decodeBase64Utf8String,
} from '../utils/decodeSearchParams';
import { useConsumeWebCommand } from './use-consume-web-command';

const useWebviewRouter = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const activeRoutePath = useSelector((state: AppState) => state.native.activeRoutePath);
  const onWebViewInitialized = useSelector((state: AppState) => state.native.onWebViewInitialized);

  useConsumeWebCommand(WebCommandEnum.Navigate, (command) => {
    if (!command.params?.path) {
      logger.warn(`native/${WebCommandEnum.Navigate} received without path — dropping`);
      return;
    }
    const { params } = command;
    dispatch(selectMapObject(null));
    if (params.path) {
      dispatch(setActiveRoutePath(params.path));
    }
    let decodedSearch: string | undefined;
    if (params.search) {
      decodedSearch = decodeBase64Utf8String(params.search);
      if (decodedSearch) {
        dispatch(setActiveRouteSearch(decodedSearch));
      }
    }
    let decodedData = {};
    if (params.data) {
      decodedData = decodeAndParseBase64Utf8String(params.data);
    }
    const routeWithSearchParams = `${params.path}${decodedSearch ?? ''}`;
    navigate(routeWithSearchParams, { state: { fromNative: true, ...decodedData } });
  });

  useEffect(() => {
    if (!onWebViewInitialized) {
      return;
    }

    if (location.state?.fromNative) {
      dispatch(dispatchNativeCommand({ name: NativeCommandEnum.RouteChanged }));
    } else {
      dispatch(
        dispatchNativeCommand({
          name: NativeCommandEnum.NavigateTo,
          params: {
            path: location.pathname,
            search: location.search,
            data: location.state,
          },
        })
      );
    }
  }, [dispatch, location, onWebViewInitialized]);

  return { activeRoutePath };
};
export { useWebviewRouter };
